import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Info, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
const questions = ['Do you need to gamble with increasing amounts of money in order to achieve the desired excitement?', 'Are you restless or irritable when attempting to cut down or stop gambling?', 'Have you made repeated unsuccessful efforts to control, cut back, or stop gambling?', 'Are you often preoccupied with gambling (e.g., having persistent thoughts of reliving past gambling experiences, handicapping or planning the next venture, thinking of ways to get money with which to gamble)?', 'Do you often gamble when feeling distressed (e.g., helpless, guilty, anxious, depressed)?', 'After losing money gambling, do you often return another day to get even ("chasing" one\'s losses)?', 'Do you lie to conceal the extent of involvement with gambling?', 'Have you jeopardized or lost a significant relationship, job, or educational or career opportunity because of gambling?', 'Do you rely on others to provide money to relieve desperate financial situations caused by gambling?'];
export function DSM5TestSection() {
  const [answers, setAnswers] = useState<Record<number, boolean | null>>({});
  const [showResult, setShowResult] = useState(false);
  const handleAnswer = (questionIndex: number, value: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };
  const answeredCount = Object.values(answers).filter(v => v !== null && v !== undefined).length;
  const isComplete = answeredCount === questions.length;
  const calculateScore = () => {
    return Object.values(answers).filter(v => v === true).length;
  };
  const getResult = (score: number) => {
    if (score <= 3) {
      return {
        title: 'Insufficient criteria to diagnose a disorder',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: <CheckCircle className="w-10 h-10 text-green-500" data-id="element-98" />,
        description: 'Based on the test results, the number of positive answers does not reach the threshold for diagnosing a gambling disorder. However, if you are concerned about your gambling behavior, please consult a specialist.'
      };
    } else {
      return {
        title: 'Possible gambling disorder',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: <AlertCircle className="w-10 h-10 text-red-500" data-id="element-99" />,
        description: 'The number of positive answers indicates a possible gambling disorder. It is strongly recommended to consult a psychologist, psychotherapist, or other qualified specialist for a professional assessment.'
      };
    }
  };
  const resetTest = () => {
    setAnswers({});
    setShowResult(false);
  };
  return <section id="dsm5-test" className="py-20 bg-[#F5F0E8]" data-id="element-100">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-101">
        {/* Info Card */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 mb-12 shadow-sm" data-id="element-102">
          <div className="flex items-start gap-4" data-id="element-103">
            <Info className="w-6 h-6 text-[#0A5E5C] flex-shrink-0 mt-1" data-id="element-104" />
            <div data-id="element-105">
              <h3 className="font-serif font-bold text-xl text-[#0A5E5C] mb-3" data-id="element-106">
                2. DSM‑5 Gambling Disorder Criteria
              </h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-107">
                These criteria are used for clinical assessment of a gambling
                disorder. In this edition, gambling addiction was officially
                classified under the category of behavioral addictions.
              </p>
              <ul className="text-sm text-gray-500 space-y-1" data-id="element-108">
                <li data-id="element-109">
                  <span className="font-medium text-gray-700" data-id="element-110">Developer:</span>{' '}
                  American Psychiatric Association
                </li>
                <li data-id="element-111">
                  <span className="font-medium text-gray-700" data-id="element-112">Year:</span> 2013
                </li>
                <li data-id="element-113">
                  <span className="font-medium text-gray-700" data-id="element-114">Source:</span>{' '}
                  Diagnostic and Statistical Manual of Mental Disorders, Fifth
                  Edition (DSM‑5)
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quiz Area */}
        {!showResult ? <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-10" data-id="element-115">
            <div className="mb-8" data-id="element-116">
              <h4 className="text-lg font-bold text-[#0A5E5C] mb-2" data-id="element-117">
                Instructions:
              </h4>
              <p className="text-gray-600" data-id="element-118">
                Answer <span className="font-bold" data-id="element-119">Yes</span> or{' '}
                <span className="font-bold" data-id="element-120">No</span>, assessing your condition
                over the last <span className="font-bold" data-id="element-121">12 months</span>.
              </p>
            </div>

            <div className="space-y-10" data-id="element-122">
              {questions.map((question, qIndex) => <div key={qIndex} className="border-b border-gray-100 pb-8 last:border-0 last:pb-0" data-id="element-123">
                  <h5 className="text-lg font-medium text-gray-800 mb-4" data-id="element-124">
                    <span className="text-[#D4A843] font-bold mr-2" data-id="element-125">
                      {qIndex + 1}.
                    </span>
                    {question}
                  </h5>
                  <div className="flex gap-3" data-id="element-126">
                    <button onClick={() => handleAnswer(qIndex, true)} className={`py-3 px-8 rounded-lg border text-sm font-medium transition-all duration-200 ${answers[qIndex] === true ? 'bg-[#0A5E5C] border-[#0A5E5C] text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-127">
                      Yes
                    </button>
                    <button onClick={() => handleAnswer(qIndex, false)} className={`py-3 px-8 rounded-lg border text-sm font-medium transition-all duration-200 ${answers[qIndex] === false ? 'bg-[#0A5E5C] border-[#0A5E5C] text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-128">
                      No
                    </button>
                  </div>
                </div>)}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-center" data-id="element-129">
              <Button variant="primary" size="lg" disabled={!isComplete} onClick={() => setShowResult(true)} className="min-w-[200px]" data-id="element-130">
                See Results
              </Button>
            </div>

            {!isComplete && <p className="text-center text-sm text-gray-400 mt-4" data-id="element-131">
                Answer all questions to see your result ({answeredCount} of{' '}
                {questions.length})
              </p>}
          </div> /* Results Area */ : <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 text-center" data-id="element-132">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-133">
              {getResult(calculateScore()).icon}
            </div>

            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-2" data-id="element-134">
              &quot;Yes&quot; answers:{' '}
              <span className="text-3xl text-[#0A5E5C]" data-id="element-135">
                {calculateScore()}
              </span>{' '}
              of {questions.length}
            </h3>

            <div className={`mt-8 p-6 rounded-xl border ${getResult(calculateScore()).bgColor} ${getResult(calculateScore()).borderColor}`} data-id="element-136">
              <h4 className={`text-2xl font-bold mb-4 ${getResult(calculateScore()).color}`} data-id="element-137">
                {getResult(calculateScore()).title}
              </h4>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-138">
                {getResult(calculateScore()).description}
              </p>
            </div>

            <div className="mt-10 flex justify-center" data-id="element-139">
              <Button variant="outline" onClick={resetTest} className="flex items-center gap-2" data-id="element-140">
                <RotateCcw className="w-4 h-4" data-id="element-141" />
                Take the test again
              </Button>
            </div>
          </div>}
      </div>
    </section>;
}